/**
 * PostHog event names for the app-install funnel. Single source so renaming
 * an event is a one-line change and the funnel dashboard stays in sync.
 *
 * Funnel:
 *   cta_viewed  (impression, fired by IntersectionObserver on banner/card)
 *   cta_clicked (click on any install CTA)
 *   native_heartbeat (native — fires once per session when /api/native-ping
 *                     succeeds; proves a given user has the native app)
 *
 * The `deep_link_resolved` / `native_first_open` / `consent_responded` events
 * will return when AppsFlyer (or equivalent attribution provider) is added
 * in a future phase.
 */

export const InstallEvents = {
  CtaViewed: 'app_install_cta_viewed',
  CtaClicked: 'app_install_cta_clicked',
  NativeHeartbeat: 'app_install_native_heartbeat',
} as const

export type InstallEvent = (typeof InstallEvents)[keyof typeof InstallEvents]

/**
 * Surfaces that emit install CTAs. Keep in sync with the `source` argument
 * of `buildOneLink` so PostHog and AppsFlyer report the same labels.
 */
export type InstallSurface =
  | 'mobile-web-banner'
  | 'landing-footer'
  | 'landing-native'
  | 'onboarding-card'
  | 'settings-tile'
  | 'get-app'
  | 'email-welcome'
  | 'email-invitation'
  | 'email-org-admin-invitation'
  | 'email-chapter-invitation'
  | 'email-dm-notification'
  | 'email-event-reminder'
  | 'email-weekly-digest'
  | 'email-connection-request'
  | 'email-install-nudge'

export interface CtaEventProps {
  surface: InstallSurface
  platform?: 'ios' | 'android' | 'desktop'
  posthog_distinct_id?: string | null
}

/** Longest legitimate surface label in use today (`email-org-admin-invitation`, 28 chars) plus headroom. */
const MAX_SURFACE_LENGTH = 64

/**
 * `/get-app`'s `?s=` param becomes the PostHog `surface` property unchecked
 * (see `app/get-app/page.tsx`). That redirect is public and unauthenticated
 * by design, so the param is attacker/proxy-controllable — an email-security
 * link scanner, a mail client that reflows HTML, or a second URL pasted
 * alongside the first can land in the query string before the click ever
 * reaches us. One observed case: `s=email-invitationhttps://www.alumnia.net/
 * get-app?platform=android`, which corrupted the email-surface breakdown.
 *
 * Rejects values that look like a URL got appended (`://`) or are
 * implausibly long, returning `null` so the caller can fall back to a known
 * label instead of writing the raw string to analytics.
 */
export function sanitizeInstallSurface(value: string | null | undefined): string | null {
  if (!value) return null
  if (value.length > MAX_SURFACE_LENGTH) return null
  if (value.includes('://')) return null
  return value
}
